import {
    lazyGetOobeFunnelEvent,
    lazyGetSendLogs,
    lazyGetSendSpecifiedLogs,
    lazyGetSendSpecifiedLogsWithConfig,
    lazyGetStartSpecifiedReproDataCollectors,
    lazyGetStopSpecifiedReproDataCollectors,
    lazyGetGetRegisteredReproDataCollectors,
    lazyGetGetRuntimeModeStatus,
} from './lazyFunctions';

import type {
    DiagnosticsLogOptions,
    DiagnosticsLogOptionsWithConfig,
    DataCollectorInfo,
    RuntimeModeStatus,
} from '@microsoft/wv2-outlook';

// Types — zero runtime cost
/* eslint-disable owa-custom-rules/forbid-package-reexport  -- (https://aka.ms/OWALintWiki)
 * Re-exporting types from @microsoft/wv2-outlook so consumers don't need a direct dependency. */
export type {
    DiagnosticsLogOptions,
    DiagnosticsLogOptionsWithConfig,
    DataCollectorInfo,
    RuntimeMode,
    RuntimeModeStatus,
} from '@microsoft/wv2-outlook';
/* eslint-enable owa-custom-rules/forbid-package-reexport */

// Constants — duplicated to avoid pulling in the side-effectful gen module.

// RuntimeMode
export const RUNTIME_MODE_SAFE_MODE = 'SafeMode' as const;
export const RUNTIME_MODE_RECOVERY_MODE = 'RecoveryMode' as const;
export const RUNTIME_MODE_CONFIG_JSON_ACTIVE = 'ConfigJsonActive' as const;

// --- oobeFunnelEvent ---

async function getOobeFunnelEvent(): Promise<((event: string) => Promise<void>) | undefined> {
    return lazyGetOobeFunnelEvent.importAndExecute();
}

export async function canCallOobeFunnelEvent(): Promise<boolean> {
    return (await getOobeFunnelEvent()) !== undefined;
}

export async function oobeFunnelEvent(event: string): Promise<void> {
    const fn = await getOobeFunnelEvent();
    if (!fn) {
        throw new Error('oobeFunnelEvent interop API is not available');
    }
    return fn(event);
}

// --- sendLogs ---

async function getSendLogs(): Promise<
    ((diagnosticSessionId: string, odsUri: string | undefined) => Promise<void>) | undefined
> {
    return lazyGetSendLogs.importAndExecute();
}

export async function canCallSendLogs(): Promise<boolean> {
    return (await getSendLogs()) !== undefined;
}

export async function sendLogs(
    diagnosticSessionId: string,
    odsUri: string | undefined
): Promise<void> {
    const fn = await getSendLogs();
    if (!fn) {
        throw new Error('sendLogs interop API is not available');
    }
    return fn(diagnosticSessionId, odsUri);
}

// --- sendSpecifiedLogs ---

async function getSendSpecifiedLogs(): Promise<
    ((options: DiagnosticsLogOptions) => Promise<void>) | undefined
> {
    return lazyGetSendSpecifiedLogs.importAndExecute();
}

export async function canCallSendSpecifiedLogs(): Promise<boolean> {
    return (await getSendSpecifiedLogs()) !== undefined;
}

export async function sendSpecifiedLogs(options: DiagnosticsLogOptions): Promise<void> {
    const fn = await getSendSpecifiedLogs();
    if (!fn) {
        throw new Error('sendSpecifiedLogs interop API is not available');
    }
    return fn(options);
}

// --- sendSpecifiedLogsWithConfig ---

async function getSendSpecifiedLogsWithConfig(): Promise<
    ((options: DiagnosticsLogOptionsWithConfig) => Promise<void>) | undefined
> {
    return lazyGetSendSpecifiedLogsWithConfig.importAndExecute();
}

export async function canCallSendSpecifiedLogsWithConfig(): Promise<boolean> {
    return (await getSendSpecifiedLogsWithConfig()) !== undefined;
}

export async function sendSpecifiedLogsWithConfig(
    options: DiagnosticsLogOptionsWithConfig
): Promise<void> {
    const fn = await getSendSpecifiedLogsWithConfig();
    if (!fn) {
        throw new Error('sendSpecifiedLogsWithConfig interop API is not available');
    }
    return fn(options);
}

// --- startSpecifiedReproDataCollectors ---

async function getStartSpecifiedReproDataCollectors(): Promise<
    ((reproDataCollectorNames: string[]) => Promise<void>) | undefined
> {
    return lazyGetStartSpecifiedReproDataCollectors.importAndExecute();
}

export async function canCallStartSpecifiedReproDataCollectors(): Promise<boolean> {
    return (await getStartSpecifiedReproDataCollectors()) !== undefined;
}

export async function startSpecifiedReproDataCollectors(
    reproDataCollectorNames: string[]
): Promise<void> {
    const fn = await getStartSpecifiedReproDataCollectors();
    if (!fn) {
        throw new Error('startSpecifiedReproDataCollectors interop API is not available');
    }
    return fn(reproDataCollectorNames);
}

// --- stopSpecifiedReproDataCollectors ---

async function getStopSpecifiedReproDataCollectors(): Promise<
    ((reproDataCollectorNames: string[]) => Promise<void>) | undefined
> {
    return lazyGetStopSpecifiedReproDataCollectors.importAndExecute();
}

export async function canCallStopSpecifiedReproDataCollectors(): Promise<boolean> {
    return (await getStopSpecifiedReproDataCollectors()) !== undefined;
}

export async function stopSpecifiedReproDataCollectors(
    reproDataCollectorNames: string[]
): Promise<void> {
    const fn = await getStopSpecifiedReproDataCollectors();
    if (!fn) {
        throw new Error('stopSpecifiedReproDataCollectors interop API is not available');
    }
    return fn(reproDataCollectorNames);
}

// --- getRegisteredReproDataCollectors ---

async function getGetRegisteredReproDataCollectors(): Promise<
    (() => Promise<DataCollectorInfo[]>) | undefined
> {
    return lazyGetGetRegisteredReproDataCollectors.importAndExecute();
}

export async function canCallGetRegisteredReproDataCollectors(): Promise<boolean> {
    return (await getGetRegisteredReproDataCollectors()) !== undefined;
}

export async function getRegisteredReproDataCollectors(): Promise<DataCollectorInfo[]> {
    const fn = await getGetRegisteredReproDataCollectors();
    if (!fn) {
        throw new Error('getRegisteredReproDataCollectors interop API is not available');
    }
    return fn();
}

// --- getRuntimeModeStatus ---

async function getGetRuntimeModeStatus(): Promise<(() => Promise<RuntimeModeStatus>) | undefined> {
    return lazyGetGetRuntimeModeStatus.importAndExecute();
}

export async function canCallGetRuntimeModeStatus(): Promise<boolean> {
    return (await getGetRuntimeModeStatus()) !== undefined;
}

export async function getRuntimeModeStatus(): Promise<RuntimeModeStatus> {
    const fn = await getGetRuntimeModeStatus();
    if (!fn) {
        throw new Error('getRuntimeModeStatus interop API is not available');
    }
    return fn();
}
